// お問い合わせ・要望・報告（メニュー・チャットの「報告・要望」から）：運営だけが読む一方通行の連絡。
// お問い合わせ（購入・アカウント）／要望／不具合報告／送った内容 の4つのタブ。スクショは3枚まで（端末で JPEG に縮めて送る）。送った人の ID・端末の情報は自動で添える。
// 返事は約束しない（不具合の修正は全体のお知らせで伝える）。遊び方の相談は茶屋のたまき（#/support・AI）へ。
import { SUPPORT } from '@hyakki/core';
import { api, ApiError } from '../api';
import { go } from '../router';
import { state } from '../state';
import { buildId, recentErrors } from '../error-log';
import { esc } from './dom';
import { icon } from './icons';
import { openModal, toast } from './overlay';
import { errorText } from './status';
import { guestBlocked } from './google-link';

type Kind = 'contact' | 'request' | 'bug';
type Tab = Kind | 'sent';
const MAX_TEXT = 1000;
const MAX_IMAGES = 3;
/** 縮めた画像の長い辺・JPEG の画質（送る大きさは1枚 800KB まで） */
const IMG_SIDE = 1600;
const IMG_LIMIT = 780_000;

/** 連絡に添える端末・画面の情報（運営だけが見る。サーバーが ID・主Lv・進み具合を足す） */
export function clientContext() {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    screen: location.hash.slice(0, 200),
    ua: navigator.userAgent,
    viewport: `${innerWidth}x${innerHeight}`,
    dpr: devicePixelRatio,
    standalone: nav.standalone === true || matchMedia('(display-mode: standalone)').matches,
    online: navigator.onLine,
    build: buildId(),
    errors: recentErrors(),
  };
}

/** 画像を長い辺 IMG_SIDE までの JPEG（data URL）に。大きければ画質を下げる */
async function shrink(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, IMG_SIDE / Math.max(bmp.width, bmp.height));
  const cv = document.createElement('canvas');
  cv.width = Math.max(1, Math.round(bmp.width * k));
  cv.height = Math.max(1, Math.round(bmp.height * k));
  cv.getContext('2d')!.drawImage(bmp, 0, 0, cv.width, cv.height);
  bmp.close?.();
  for (const q of [0.82, 0.7, 0.55, 0.4]) {
    const url = cv.toDataURL('image/jpeg', q);
    if ((url.length * 3) / 4 < IMG_LIMIT) return url;
  }
  throw new Error('too_big');
}

const KIND_LABEL: Record<Kind, string> = { contact: 'お問い合わせ', request: '要望', bug: '不具合報告' };
const PLACEHOLDER: Record<Kind, string> = {
  contact: '購入・アカウントのことなど（購入なら日時と商品名も）',
  request: 'あったらうれしい機能・遊びやすくしてほしいところなど',
  bug: 'いつ・どの画面で・何をしたら・どうなったか（スクショがあると助かります）',
};
const fmtDate = (t: number) => {
  const d = new Date(t);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** お問い合わせ・要望・報告の小窓（ゲストは連携の案内） */
export function openContact(first: Tab = 'bug') {
  if (guestBlocked('お問い合わせ・要望・不具合報告は、Google連携すると送れます。', 'contact')) return;
  let tab: Tab = first;
  const draft: Record<Kind, { text: string; images: string[] }> = { contact: { text: '', images: [] }, request: { text: '', images: [] }, bug: { text: '', images: [] } };
  let sending = false;
  const m = openModal(
    `<div class="contact">
      <div class="tabs seg-tabs ct-tabs" role="tablist">${(['contact', 'request', 'bug', 'sent'] as Tab[])
        .map((t) => `<button class="tab-btn" role="tab" data-ct="${t}">${t === 'sent' ? '送った内容' : KIND_LABEL[t]}</button>`)
        .join('')}</div>
      <div class="ct-body"></div>
      <p class="ct-note">すべてのお問い合わせに返信できない場合があります。不具合の修正は、全体のお知らせでお伝えします。</p>
      <p class="ct-alt">遊び方は<a class="ct-okami" href="https://honulabs.net/hyakki/wiki/" target="_blank" rel="noopener">攻略wiki</a>・<button type="button" class="ct-okami" data-okami>${esc(SUPPORT.name)}に相談</button></p>
    </div>`,
    { title: 'お問い合わせ・要望・報告', className: 'contact-wrap' },
  );
  const body = m.body.querySelector<HTMLElement>('.ct-body')!;

  const formHtml = (k: Kind) => {
    const d = draft[k];
    return `<form class="ct-form" data-kind="${k}">
      <textarea class="ct-text" maxlength="${MAX_TEXT}" rows="6" placeholder="${esc(PLACEHOLDER[k])}">${esc(d.text)}</textarea>
      <div class="ct-row"><small class="ct-count">${[...d.text].length}/${MAX_TEXT}</small></div>
      <div class="ct-imgs">${d.images.map((u, i) => `<span class="ct-img"><img src="${u}" alt="添付${i + 1}"><button type="button" class="ct-img-x" data-rm="${i}" aria-label="外す">${icon('close')}</button></span>`).join('')}${
        d.images.length < MAX_IMAGES
          ? `<label class="ct-add">${icon('plus')}<span>画像</span><input type="file" accept="image/*" ${MAX_IMAGES - d.images.length > 1 ? 'multiple' : ''} hidden></label>`
          : ''
      }</div>
      <p class="ct-auto">${icon('info')}送った人のIDと端末の情報が自動で添えられます</p>
      <button type="submit" class="btn primary wide ct-send" ${d.text.trim() ? '' : 'disabled'}>送る</button>
    </form>`;
  };

  const drawSent = async () => {
    body.innerHTML = '<p class="muted ct-empty">読み込み中…</p>';
    try {
      const { reports } = await api.feedbackList();
      if (tab !== 'sent' || !m.el.isConnected) return;
      body.innerHTML = reports.length
        ? `<ul class="ct-sent">${reports
            .map(
              (r) => `<li><div class="ct-sent-head"><span class="tag ${r.kind === 'bug' ? 'red' : r.kind === 'contact' ? 'ok' : ''}">${KIND_LABEL[r.kind] ?? '報告'}</span><small>${fmtDate(r.createdAt)}</small></div>
                <p>${esc(r.text)}</p>${r.images ? `<div class="ct-imgs">${Array.from({ length: r.images }, (_, i) => `<a class="ct-img" href="/api/feedback/${r.id}/image/${i}" target="_blank" rel="noopener"><img src="/api/feedback/${r.id}/image/${i}" alt="添付${i + 1}" loading="lazy"></a>`).join('')}</div>` : ''}</li>`,
            )
            .join('')}</ul><p class="muted small ct-sent-note">最近送った50件までを新しい順に表示します。</p>`
        : '<p class="muted ct-empty">まだ送ったものはありません</p>';
    } catch (e) {
      body.innerHTML = `<p class="bad ct-empty">${esc(errorText(e))}</p>`;
    }
  };

  const draw = () => {
    m.body.querySelectorAll<HTMLElement>('[data-ct]').forEach((b) => {
      b.classList.toggle('on', b.dataset.ct === tab);
      b.setAttribute('aria-selected', String(b.dataset.ct === tab));
    });
    if (tab === 'sent') return void drawSent();
    body.innerHTML = formHtml(tab);
  };
  draw();

  m.body.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const ct = t.closest<HTMLElement>('[data-ct]');
    if (ct) {
      tab = ct.dataset.ct as Tab;
      return draw();
    }
    if (t.closest('[data-okami]')) {
      m.close();
      return go('support');
    }
    const rm = t.closest<HTMLElement>('[data-rm]');
    if (rm && tab !== 'sent') {
      draft[tab].images.splice(Number(rm.dataset.rm), 1);
      return draw();
    }
  });
  m.body.addEventListener('input', (e) => {
    const ta = (e.target as HTMLElement).closest<HTMLTextAreaElement>('.ct-text');
    if (!ta || tab === 'sent') return;
    draft[tab].text = ta.value;
    m.body.querySelector('.ct-count')!.textContent = `${[...ta.value].length}/${MAX_TEXT}`;
    m.body.querySelector<HTMLButtonElement>('.ct-send')!.disabled = !ta.value.trim() || sending;
  });
  m.body.addEventListener('change', async (e) => {
    const input = (e.target as HTMLElement).closest<HTMLInputElement>('input[type=file]');
    if (!input || tab === 'sent') return;
    const k = tab;
    const files = [...(input.files ?? [])].slice(0, MAX_IMAGES - draft[k].images.length);
    for (const f of files) {
      try {
        draft[k].images.push(await shrink(f));
      } catch {
        toast('この画像は添付できませんでした。', 'error');
      }
    }
    if (tab === k) draw();
  });
  m.body.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (tab === 'sent' || sending) return;
    const k = tab;
    const d = draft[k];
    if (!d.text.trim()) return;
    sending = true;
    const btn = m.body.querySelector<HTMLButtonElement>('.ct-send')!;
    btn.disabled = true;
    btn.textContent = '送っています…';
    try {
      await api.feedbackSend(k, d.text.trim(), d.images, clientContext());
      draft[k] = { text: '', images: [] };
      toast(`${KIND_LABEL[k]}を送りました。ありがとうございます。`, 'good', 3000);
      tab = 'sent';
      draw();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      const map: Record<string, string> = {
        rate: '短い間に送れる数を超えました。しばらくしてからお送りください。',
        text_length: `${MAX_TEXT}文字までです。`,
        image: '画像を送れませんでした。別の画像でお試しください。',
        image_disabled: '今は画像を送れません。文だけでお送りください。',
      };
      toast(map[code] ?? errorText(err), 'error', 4000);
      btn.disabled = false;
      btn.textContent = '送る';
    } finally {
      sending = false;
    }
  });
  return m;
}

/** ゲストかどうか（メニューで鍵を出すとき用） */
export const contactLocked = () => !!state.me?.user.guest;
